<template>
  <div v-if="shouldShowModal" class="payment-modal-overlay" :class="modalType">
    <div class="payment-modal" :class="modalType">
      <div class="modal-header">
        <h2>{{ modalTitle }}</h2>
        <button 
          v-if="canDismiss" 
          @click="dismissModal" 
          class="close-button"
          aria-label="Close modal"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="icon-container">
          <i class="fa fa-clock-o" v-if="freeTrial > 0"></i>
          <i class="fa fa-exclamation-triangle" v-else-if="daysExpired <= 2"></i>
          <i class="fa fa-ban" v-else></i>
        </div>
        
        <div class="message-content">
          <p v-if="freeTrial > 0" class="trial-remaining">
            Your free trial expires in <strong>{{ freeTrial }}</strong> {{ freeTrial === 1 ? 'day' : 'days' }}.
          </p>
          <p v-else-if="daysExpired <= 2" class="trial-expired-dismissable">
            Thank you for trialling BookedSolid! Your trial expired {{ daysExpired }} {{ daysExpired === 1 ? 'day' : 'days' }} ago.
            To continue using our platform and access all features, please upgrade your account.
          </p>
          <p v-else class="trial-expired-blocked">
            Your BookedSolid trial expired over a week ago. To continue using our platform, 
            you must upgrade your account. All features are currently blocked until payment is completed.
          </p>
          
          <div class="benefits">
            <h3>Thank you for trying BookedSolid! 🎉</h3>
            <p class="thank-you-message">
              We hope you've enjoyed exploring all the features that BookedSolid has to offer. 
              To continue having access to all your current features, 
              please upgrade your account today.
            </p>
            <p class="upgrade-message">
              Your upgrade helps us continue providing you with the best possible service and support.
            </p>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <div class="billing-options">
          <button @click="subscribe('monthly')" :disabled="loadingInterval !== null" class="subscribe-button">
            <i v-if="loadingInterval === 'monthly'" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-calendar-o"></i>
            Pay monthly
          </button>
          <button @click="subscribe('yearly')" :disabled="loadingInterval !== null || yearlyDiscountPercentage === null" class="subscribe-button yearly-button">
            <i v-if="loadingInterval === 'yearly'" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-calendar-check-o"></i>
            Pay yearly<span v-if="yearlyDiscountPercentage !== null"> ({{ yearlyDiscountPercentage }}% discount)</span>
          </button>
        </div>
        <button 
          v-if="canDismiss" 
          @click="dismissModal" 
          class="dismiss-button"
        >
          Continue Trial
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { createPayment, getSubscriptionOptions } from '@/app/admin-routes';

export default {
  name: 'PaymentModal',
  props: {
    hasPaid: {
      type: Boolean,
      required: true
    },
    freeTrial: {
      type: Number,
      required: true
    },
    mobile: {
      type: Boolean,
      required: true
    },
    routeName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isDismissed: false,
      loadingInterval: null,
      yearlyDiscountPercentage: null,
    };
  },
  computed: {
    shouldShowModal() {
      // Don't show on login or mobile
      if (this.mobile || this.routeName === 'login') {
        return false;
      }
      
      // Don't show if already paid
      if (this.hasPaid) {
        return false;
      }
      
      // Don't show if dismissed and can be dismissed
      if (this.isDismissed && this.canDismiss) {
        return false;
      }
      
      // Show warning when trial ends in 3 days or less
      // Show dismissable modal when expired but <= 7 days
      // Show blocking modal when expired > 7 days
      return this.freeTrial <= 3; // Show when 3 days or less remaining, or expired
    },
    
    daysExpired() {
      return this.freeTrial < 0 ? Math.abs(this.freeTrial) : 0;
    },
    
    modalType() {
      if (this.freeTrial > 0) {
        return 'warning'; // Trial active but ending soon
      } else if (this.daysExpired <= 2) {
        return 'expired-dismissable'; // Trial expired but can dismiss
      } else {
        return 'expired-blocking'; // Trial expired > 1 week, blocks usage
      }
    },
    
    canDismiss() {
      // Can dismiss if trial is active or expired less than a week
      return this.freeTrial > 0 || this.daysExpired <= 2;
    },
    
    modalTitle() {
      if (this.freeTrial > 0) {
        return 'Free Trial Ending Soon';
      } else if (this.daysExpired <= 2) {
        return 'Free Trial Expired';
      } else {
        return 'Account Upgrade Required';
      }
    }
  },
  methods: {
    dismissModal() {
      if (this.canDismiss) {
        this.isDismissed = true;
      }
    },
    
    async loadSubscriptionOptions() {
      const options = await getSubscriptionOptions();
      if (options?.success) {
        this.yearlyDiscountPercentage = options.yearlyDiscountPercentage;
      }
    },

    async subscribe(billingInterval) {
      this.loadingInterval = billingInterval;
      await createPayment(billingInterval);
      this.loadingInterval = null;
    }
  },
  watch: {
    // Reset dismissal when freeTrial changes (in case user comes back later)
    freeTrial() {
      this.isDismissed = false;
    },
    shouldShowModal(isVisible) {
      if (isVisible && this.yearlyDiscountPercentage === null) {
        this.loadSubscriptionOptions();
      }
    }
  },
  mounted() {
    if (this.shouldShowModal) this.loadSubscriptionOptions();
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.payment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
  
  &.expired-blocking {
    background-color: rgba(0, 0, 0, 0.95);
  }
}

.payment-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  &.warning {
    border-top: 4px solid #fbbf24;
  }
  
  &.expired-dismissable {
    border-top: 4px solid #f59e0b;
  }
  
  &.expired-blocking {
    border-top: 4px solid #ef4444;
    animation: shake 0.5s ease-in-out;
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
  
  h2 {
    margin: 0;
    color: #1f2937;
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  .close-button {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #f3f4f6;
      color: #374151;
    }
    
    i {
      font-size: 1.2rem;
    }
  }
}

.modal-body {
  padding: 24px;
  text-align: center;
  
  .icon-container {
    margin-bottom: 20px;
    
    i {
      font-size: 3rem;
      
      &.fa-clock-o {
        color: #fbbf24;
      }
      
      &.fa-exclamation-triangle {
        color: #f59e0b;
      }
      
      &.fa-ban {
        color: #ef4444;
      }
    }
  }
  
  .message-content {
    p {
      font-size: 1.1rem;
      line-height: 1.6;
      color: #374151;
      margin-bottom: 24px;
      
      strong {
        color: #1f2937;
        font-weight: 600;
      }
    }
    
    .trial-remaining {
      color: #d97706;
    }
    
    .trial-expired-dismissable {
      color: #92400e;
    }
    
    .trial-expired-blocked {
      color: #dc2626;
      font-weight: 500;
    }
  }
  
  .benefits {
    background: #f5f7fb;
    border-radius: 12px;
    padding: 20px;
    margin-top: 20px;
    text-align: center;
    
    h3 {
      margin: 0 0 16px 0;
      color: #1f2937;
      font-size: 1.2rem;
      font-weight: 600;
      text-align: center;
    }
    
    .thank-you-message {
      font-size: 1rem;
      line-height: 1.6;
      color: #374151;
      margin-bottom: 16px;
      text-align: center;
    }
    
    .upgrade-message {
      font-size: 0.95rem;
      line-height: 1.5;
      color: #6b7280;
      margin-bottom: 0;
      font-style: italic;
      text-align: center;
    }
  }
}

.modal-footer {
  padding: 0 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;

  .billing-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  
  button {
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
  
  .subscribe-button {
    background: linear-gradient(135deg, $colourP 0%, $colourS 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($colourP, 0.3);
    }
  }
  
  .dismiss-button {
    background: #f3f4f6;
    color: #6b7280;
    align-self: center;
    
    &:hover {
      background: #e5e7eb;
      color: #374151;
    }
  }
}

// Responsive adjustments
@media (max-width: 600px) {
  .payment-modal {
    margin: 20px;
    width: calc(100% - 40px);
  }
  
  .modal-header {
    padding: 20px 20px 0;
    
    h2 {
      font-size: 1.3rem;
    }
  }
  
  .modal-body {
    padding: 20px;
    
    .icon-container i {
      font-size: 2.5rem;
    }
    
    .message-content p {
      font-size: 1rem;
    }
  }
  
  .modal-footer {
    padding: 0 20px 20px;

    .billing-options {
      grid-template-columns: 1fr;
    }
    
    button {
      width: 100%;
    }
  }
}
</style>
